import {
  Badge,
  Button,
  ButtonVariant,
  Grid,
  Icon,
  IconColor,
  IconName,
  IconSize,
  SnackbarVariant,
  Text,
  TextSize,
  TextWeight,
  makeStyles,
  useSnackbar
} from '@focaldata/cin-ui-components'
import { Link, useNavigate } from 'react-router-dom'
import { getStatusBadge } from '../../../controls/ProjectListItem/ProjectListItem.utils'
import { ProjectStatus } from '../../../data/gql-gen/dashboard/graphql'
import { useProjectId } from '../../../hooks/useProjectId'
import { useGetProjectByProjectId } from '../../../modules/Project/Project.hooks'
import { EditableProjectTitle } from '../../../modules/Project/ProjectNameAndStatus/EditableProjectTitle'
import { ProjectBreadcrumb } from '../ProjectBreadcrumb'
import { useCreateWave, useDuplicateWave, useWaves } from './useWaves'

const useStyles = makeStyles()({
  page: { padding: '24px 32px' },
  // Scrolls rather than growing the page: a tracker can have many waves.
  waves: {
    marginTop: 24,
    maxWidth: 820,
    maxHeight: 'calc(100vh - 260px)',
    overflowY: 'auto'
  },
  wave: {
    display: 'grid',
    gridTemplateColumns: '3fr 1fr',
    alignItems: 'center',
    padding: '14px 16px',
    border: '1px solid #E5EAF5',
    borderRadius: 12,
    marginTop: 8,
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': { borderColor: 'var(--primary)' }
  }
})

/**
 * Tracker project screen, at /project/:projectId — the path multi-market also
 * uses, dispatched on project type by ProjectRoutes. A tracker starts with no
 * waves, so the page is near-empty until the first is created here.
 */
export const TrackerProjectPage = () => {
  const { classes } = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const projectId = useProjectId()
  const { project, loading } = useGetProjectByProjectId()
  const { waves } = useWaves(projectId)
  const { createWave, loading: creatingWave } = useCreateWave(projectId)
  const { duplicateWave, loading: duplicatingWave } =
    useDuplicateWave(projectId)

  // One draft wave per project, enforced by the backend. The button is disabled
  // rather than failing, but the error still shows: another editor could create
  // one between load and click.
  const draftWave = waves.find((wave) => wave.status === ProjectStatus.Draft)

  // The first wave adopts the project's survey and gets its questionnaire when
  // opened (WaveQuestionnaireGuard). Every later wave copies the latest one.
  const handleCreateWave = async () => {
    try {
      const wave =
        waves.length === 0 ? await createWave() : await duplicateWave()
      navigate(`/project/${projectId}/wave/${wave.waveId}`)
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to create wave',
        { variant: SnackbarVariant.Error }
      )
    }
  }

  return (
    <div>
      <ProjectBreadcrumb
        activeStep={project?.name ?? 'Project'}
        steps={[{ label: project?.name ?? 'Project' }]}
      />

      <div className={classes.page}>
        <Grid container justifyContent="space-between" alignItems="center">
          <EditableProjectTitle
            name={loading ? 'Loading…' : project?.name ?? 'Project'}
            projectId={project?.projectId}
            size={TextSize.xl}
            weight={TextWeight.Bold}
          />
          {/* The title sits on the wrapper: a disabled MUI button has
              pointer-events off, so it never receives the hover itself. */}
          <span
            title={
              draftWave
                ? `${draftWave.label} is still a draft. A project can only have one draft wave at a time.`
                : undefined
            }
          >
            <Button
              variant={ButtonVariant.PrimaryFilled}
              startIcon={
                <Icon
                  name={IconName.Add}
                  iconColor={IconColor.White}
                  size={IconSize.Large}
                />
              }
              disabled={
                creatingWave || duplicatingWave || draftWave !== undefined
              }
              onClick={handleCreateWave}
            >
              Create new wave
            </Button>
          </span>
        </Grid>

        {waves.length > 0 && (
          <div className={classes.waves}>
            {waves.map((wave) => (
              <Link
                key={wave.waveId}
                to={`/project/${projectId}/wave/${wave.waveId}`}
                className={classes.wave}
              >
                <Text size={TextSize.ms}>{wave.label}</Text>
                <span>
                  <Badge {...getStatusBadge(wave.status)} />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
